import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as api from '../api/api';
import { formatDisplayDate } from '../utils/DateUtil';
import './ActivityList.css';

const PAGE_SIZE = 20;
const LIST_METRICS = {
    distance: { label: 'Distance', unit: 'km' },
    time: { label: 'Time', unit: '' },
};
const formatValue = (value, metric) => {
    if (metric === 'time') {
        const totalMinutes = Math.round(value / 60);
        return `${Math.floor(totalMinutes / 60)}h ${String(totalMinutes % 60).padStart(2, '0')}m`;
    }
    if (metric === 'distance') {
        return value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }
    return Math.round(value).toLocaleString();
};
// Long enough for the smooth scroll onto the focused day to have settled, so
// the "load more recent" sentinel above the list isn't tripped by the very
// scroll that's moving away from it.
const FOCUS_SCROLL_SETTLE_MS = 600;

const ActivityList = () => {
    // ?date=YYYY-MM-DD (how the plan links a day over to here): start the
    // list at that day instead of at the newest activity, then scroll to and
    // highlight it. Everything more recent than that day is still reachable
    // by scrolling back up, the same way older ones are by scrolling down.
    const [searchParams] = useSearchParams();
    const focusDate = searchParams.get('date');

    const [activities, setActivities] = useState([]);
    // The two ends of what's currently loaded, as positions in the full
    // newest-first listing: firstOffset is where activities[0] sits (0 once
    // the newest activity of all is on the page), nextOffset is the one
    // after the last.
    const [firstOffset, setFirstOffset] = useState(0);
    const [nextOffset, setNextOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingNewer, setIsLoadingNewer] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState('');
    // One flag covering both directions - only ever one page is in flight,
    // so loading upward and downward can't race each other's offsets.
    const isFetchingRef = useRef(false);
    const sentinelRef = useRef(null);
    const newerSentinelRef = useRef(null);
    const hasLoadedFirstPageRef = useRef(false);
    const focusRowRef = useRef(null);
    const hasScrolledToFocusRef = useRef(false);
    const [canLoadNewer, setCanLoadNewer] = useState(false);
    // Page height captured just before a page is prepended, so the scroll
    // position can be nudged down by however much taller the page got.
    const prependScrollHeightRef = useRef(null);

    const loadNextPage = useCallback(() => {
        // Only the first request resolves the date to an offset; every page
        // after it continues from the offset that request reported back.
        // The first page also ignores the hasMore/nextOffset this callback
        // closed over, since a restart (see below) is exactly the case where
        // they're the previous listing's, not this one's.
        const isFirstPage = !hasLoadedFirstPageRef.current;
        if (isFetchingRef.current || (!hasMore && !isFirstPage)) return;
        isFetchingRef.current = true;
        hasLoadedFirstPageRef.current = true;
        setIsLoading(true);
        api.getActivities(isFirstPage
            ? (focusDate ? { limit: PAGE_SIZE, date: focusDate } : { limit: PAGE_SIZE, offset: 0 })
            : { limit: PAGE_SIZE, offset: nextOffset })
            .then(({ data }) => {
                setActivities((currentActivities) => isFirstPage ? data.activities : [...currentActivities, ...data.activities]);
                // data.offset is where this page actually started, which for
                // a ?date= request is the backend's answer to how far down
                // the listing that day is - i.e. how much sits above it.
                if (isFirstPage) setFirstOffset(data.offset ?? 0);
                setNextOffset(data.nextOffset);
                setHasMore(data.hasMore);
                setError('');
            })
            .catch((e) => {
                setError(api.getErrorMessage(e, 'Unable to load activities.'));
                setHasMore(false);
            })
            .finally(() => {
                isFetchingRef.current = false;
                setIsLoading(false);
                setHasLoadedOnce(true);
            });
    }, [focusDate, hasMore, nextOffset]);

    // The other direction: after jumping to a day, the activities more
    // recent than it are still there to be scrolled back up into, a page at
    // a time, until the newest of all is reached.
    const loadPreviousPage = useCallback(() => {
        if (isFetchingRef.current || firstOffset <= 0) return;
        const previousOffset = Math.max(0, firstOffset - PAGE_SIZE);
        const limit = firstOffset - previousOffset;
        isFetchingRef.current = true;
        setIsLoadingNewer(true);
        api.getActivities({ limit, offset: previousOffset })
            .then(({ data }) => {
                // Captured before the prepend lands, for the layout effect
                // below to keep whatever is being read where it is on screen
                // rather than letting it jump down the page.
                prependScrollHeightRef.current = document.documentElement.scrollHeight;
                setActivities((currentActivities) => [...data.activities, ...currentActivities]);
                setFirstOffset(previousOffset);
                setError('');
            })
            .catch((e) => {
                // firstOffset is deliberately left alone: the control above
                // the list stays put, so this can simply be retried.
                setError(api.getErrorMessage(e, 'Unable to load more recent activities.'));
            })
            .finally(() => {
                isFetchingRef.current = false;
                setIsLoadingNewer(false);
            });
    }, [firstOffset]);

    // Hold the scroll position steady across a prepend - without this the
    // newly-inserted page pushes everything below it down by its own height,
    // which reads as the list jumping away mid-scroll.
    useLayoutEffect(() => {
        if (prependScrollHeightRef.current === null) return;
        const heightBefore = prependScrollHeightRef.current;
        prependScrollHeightRef.current = null;
        const addedHeight = document.documentElement.scrollHeight - heightBefore;
        if (addedHeight) window.scrollBy(0, addedHeight);
    }, [activities]);

    // Load the first page on mount, and start the listing over whenever the
    // focused day changes - including "Back to latest" clearing it, which
    // stays on this same route and so never remounts the component.
    useEffect(() => {
        isFetchingRef.current = false;
        hasLoadedFirstPageRef.current = false;
        hasScrolledToFocusRef.current = false;
        prependScrollHeightRef.current = null;
        setCanLoadNewer(false);
        setFirstOffset(0);
        setNextOffset(0);
        setHasMore(true);
        setHasLoadedOnce(false);
        setError('');
        loadNextPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusDate]);

    // Jump to the focused day once its page has arrived. Guarded so later
    // pages loading in (in either direction) don't yank the view back to it.
    useEffect(() => {
        if (hasScrolledToFocusRef.current) return undefined;
        if (!focusDate) {
            // Nothing to scroll to, and with the listing starting at its
            // newest activity there's nothing above the first page either.
            hasScrolledToFocusRef.current = true;
            setCanLoadNewer(true);
            return undefined;
        }
        if (!focusRowRef.current) return undefined;
        focusRowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
        hasScrolledToFocusRef.current = true;
        const timer = setTimeout(() => setCanLoadNewer(true), FOCUS_SCROLL_SETTLE_MS);
        return () => clearTimeout(timer);
    }, [focusDate, activities]);

    // Load older pages live as the sentinel at the bottom of the list scrolls into view.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) loadNextPage();
        }, { rootMargin: '200px' });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadNextPage]);

    // ...and more recent ones as the sentinel above the list scrolls into view.
    useEffect(() => {
        const sentinel = newerSentinelRef.current;
        if (!sentinel || !canLoadNewer) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) loadPreviousPage();
        }, { rootMargin: '200px' });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [canLoadNewer, loadPreviousPage]);

    // The first activity on the focused day is what gets scrolled to; every
    // activity that day is highlighted along with it.
    const focusIndex = focusDate ? activities.findIndex((activity) => activity.date === focusDate) : -1;
    const hasNewer = firstOffset > 0;

    return (
        <main className="activity-list-page">
            <header className="graph-heading">
                <div><span className="eyebrow">Activities</span><h1>All activities</h1></div>
                <p>{activities.length} loaded</p>
            </header>
            {focusDate && (
                <div className="activity-list-focus-banner" role="status">
                    <span>
                        {hasLoadedOnce && focusIndex === -1
                            ? <>No activities on <b>{formatDisplayDate(`${focusDate}T00:00:00`)}</b> - showing the nearest ones before it.</>
                            : <>Showing activities from <b>{formatDisplayDate(`${focusDate}T00:00:00`)}</b>.</>}
                        {hasNewer && <> Scroll up for more recent ones.</>}
                    </span>
                    <Link to="/activity">Back to latest ›</Link>
                </div>
            )}
            {hasLoadedOnce && hasNewer && (
                <>
                    <div ref={newerSentinelRef} className="activity-list-sentinel" aria-hidden="true" />
                    {/* A real control as well as a sentinel: scrolling up
                        loads the next page back on its own, but until the
                        page is tall enough to scroll there'd be no way to
                        ask for it. */}
                    <button type="button" className="activity-list-load-newer" onClick={loadPreviousPage} disabled={isLoadingNewer}>
                        {isLoadingNewer ? 'Loading more recent...' : '↑ Load more recent activities'}
                    </button>
                </>
            )}
            {!hasLoadedOnce && isLoading ? <p className="activity-list-message">Loading activities...</p>
                : error && activities.length === 0 ? <p className="activity-list-message">{error}</p>
                    : activities.length === 0 ? <p className="activity-list-message">No activities found.</p>
                        : <div className="activity-list">{activities.map((activity, index) => (
                            <Link
                                key={activity.id}
                                to={`/activity/${activity.id}`}
                                ref={index === focusIndex ? focusRowRef : undefined}
                                className={focusDate && activity.date === focusDate ? 'activity-list-focused' : undefined}
                            >
                                <span>{formatDisplayDate(activity.date)}</span>
                                <strong>{activity.name}</strong>
                                <small>{activity.type}</small>
                                <div className="activity-metrics">
                                    {Object.keys(LIST_METRICS).map((metric) => (
                                        <span key={metric}>{LIST_METRICS[metric].label}: <b>{activity.metrics[metric] === null || activity.metrics[metric] === undefined ? '—' : `${formatValue(activity.metrics[metric], metric)}${LIST_METRICS[metric].unit ? ` ${LIST_METRICS[metric].unit}` : ''}`}</b></span>
                                    ))}
                                </div>
                                <span aria-hidden="true">›</span>
                            </Link>
                        ))}</div>}
            <div ref={sentinelRef} className="activity-list-sentinel" aria-hidden="true" />
            {hasLoadedOnce && isLoading && <p className="activity-list-message activity-list-loading-more">Loading more...</p>}
            {!hasMore && activities.length > 0 && <p className="activity-list-message activity-list-end">No more activities.</p>}
            {error && activities.length > 0 && <p className="activity-list-message">{error}</p>}
        </main>
    );
};

export default ActivityList;
